// /admin/quality-profiles/:profileID — rule activation matrix (T120).
//
// Layout:
//   - Header: profile name, language chip, back link to quality-profiles list.
//   - Toolbar: search by rule id/name, language filter, show-active-only toggle.
//   - Rule table:
//       checkbox | rule_id | name | default severity | active severity | params
//   - Each active rule shows an "active severity" select (defaults to rule's
//     own severity) and a collapsible "params" editor (key=value text inputs).
//   - Save button calls PUT /admin/quality-profiles/{id}/rules atomically.
//
// The page merges two data sources:
//   - GET /api/v1/rules        → full rule catalog (static, filtered by language)
//   - GET /admin/…/{id}/rules  → active bindings for this profile

import { FormEvent, useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Layers,
  Loader2,
  Save,
  Search,
  Settings,
} from 'lucide-react';
import {
  ApiError,
  fetcher,
  ProfileRule,
  profileRulesApi,
  QualityProfile,
  Rule,
  RuleListResponse,
  RULES_CATALOG_URL,
  ISSUE_SEVERITIES,
} from '../lib/api';
import { PageHeader } from '../components/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Local editable state for one rule row. */
interface RowState {
  active: boolean;
  severityOverride: string; // '' = use rule default
  params: { key: string; value: string }[];
}

type RowMap = Record<string, RowState>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function profileRulesToMap(active: ProfileRule[]): RowMap {
  const out: RowMap = {};
  for (const r of active) {
    out[r.rule_id] = {
      active: true,
      severityOverride: r.severity_override ?? '',
      params: Object.entries(r.params ?? {}).map(([key, value]) => ({ key, value })),
    };
  }
  return out;
}

function rowMapToProfileRules(rows: RowMap): ProfileRule[] {
  return Object.entries(rows)
    .filter(([, s]) => s.active)
    .map(([rule_id, s]) => {
      const params: Record<string, string> = {};
      for (const { key, value } of s.params) {
        if (key.trim()) params[key.trim()] = value;
      }
      return { rule_id, severity_override: s.severityOverride, params };
    });
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminQualityProfileEditor() {
  const { profileID } = useParams<{ profileID: string }>();

  const { data: profile, error: profileErr } =
    useSWR<QualityProfile>(
      profileID ? `/api/v1/admin/quality-profiles/${encodeURIComponent(profileID)}` : null,
      fetcher,
      { revalidateOnFocus: false },
    );

  const { data: catalogResp, error: catalogErr } =
    useSWR<RuleListResponse>(RULES_CATALOG_URL, fetcher, { revalidateOnFocus: false });

  const { data: activeRules, error: activeErr } =
    useSWR<ProfileRule[]>(
      profileID
        ? `/api/v1/admin/quality-profiles/${encodeURIComponent(profileID)}/rules`
        : null,
      fetcher,
      { revalidateOnFocus: false },
    );

  const loading = !profile || !catalogResp || !activeRules;
  const loadErr = profileErr ?? catalogErr ?? activeErr;

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin · quality profiles"
        icon={<Layers />}
        title={profile ? profile.name : 'Profile Editor'}
        meta={
          profile ? (
            <span className="px-2 py-0.5 rounded text-xs font-mono bg-accent/10 text-accent border border-accent/20">
              {profile.language}
            </span>
          ) : null
        }
        description="Toggle rules on or off and override severity or parameters for this profile."
        actions={
          <Link
            to="/admin/quality-profiles"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <Settings className="h-3.5 w-3.5" aria-hidden="true" />
            Quality Profiles
          </Link>
        }
      />

      {(() => {
        if (loadErr) {
          return <ErrorBanner error={loadErr} />;
        } else if (loading) {
          return <LoadingCard />;
        } else {
          return (
            <MatrixEditor
              profile={profile}
              catalog={catalogResp.items}
              initialActive={activeRules}
              profileID={profileID!}
            />
          );
        }
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matrix editor
// ---------------------------------------------------------------------------

interface MatrixEditorProps {
  readonly profile: QualityProfile;
  readonly catalog: Rule[];
  readonly initialActive: ProfileRule[];
  readonly profileID: string;
}

function MatrixEditor({ profile, catalog, initialActive, profileID }: MatrixEditorProps) {
  const [rows, setRows] = useState<RowMap>(() => profileRulesToMap(initialActive));
  const [search, setSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Filter catalog to profile language (or all if language is '*').
  const langRules = useMemo(() => {
    if (profile.language === '*') return catalog;
    return catalog.filter((r) => r.language === profile.language || r.language === '*');
  }, [catalog, profile.language]);

  // Apply search + active-only filter.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return langRules.filter((r) => {
      if (showActiveOnly && !rows[r.id]?.active) return false;
      if (q && !r.id.toLowerCase().includes(q) && !r.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [langRules, rows, search, showActiveOnly]);

  const activeCount = useMemo(
    () => Object.values(rows).filter((s) => s.active).length,
    [rows],
  );

  const toggleRule = useCallback((ruleID: string) => {
    setRows((prev) => {
      const cur = prev[ruleID];
      if (cur?.active) {
        return { ...prev, [ruleID]: { ...cur, active: false } };
      }
      return {
        ...prev,
        [ruleID]: cur
          ? { ...cur, active: true }
          : { active: true, severityOverride: '', params: [] },
      };
    });
    setSaved(false);
  }, []);

  const updateRow = useCallback((ruleID: string, patch: Partial<RowState>) => {
    setRows((prev) => ({
      ...prev,
      [ruleID]: { ...prev[ruleID], ...patch },
    }));
    setSaved(false);
  }, []);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveErr(null);
    try {
      await profileRulesApi.set(profileID, rowMapToProfileRules(rows));
      setSaved(true);
    } catch (err) {
      setSaveErr(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave}>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by rule id or name…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border bg-bg-inset text-sm text-fg placeholder-fg-subtle focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-fg-muted cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            className="accent-accent"
          />
          Active only ({activeCount})
        </label>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold uppercase tracking-[0.08em] bg-accent text-accent-fg hover:opacity-90 border border-accent/40 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
        >
          {(() => {
            if (saved) {
              return <><Check className="h-3.5 w-3.5" /> Saved</>;
            } else if (saving) {
              return <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>;
            } else {
              return <><Save className="h-3.5 w-3.5" /> Save changes</>;
            }
          })()}
        </button>
      </div>

      {saveErr && (
        <div role="alert" className="mb-4 px-4 py-3 rounded-md bg-danger/10 border border-danger/30 text-xs text-danger flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {saveErr}
        </div>
      )}

      {/* Rule table */}
      <div className="orb-card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border bg-bg-inset grid grid-cols-[2rem_1fr_7rem_8rem] gap-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
          <span />
          <span>Rule</span>
          <span className="text-center">Default sev.</span>
          <span>Active sev.</span>
        </div>

        {visible.length === 0 ? (
          <p className="px-4 py-8 text-sm text-fg-muted text-center">
            No rules match the current filter.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((rule) => {
              const state = rows[rule.id] ?? { active: false, severityOverride: '', params: [] };
              return (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  state={state}
                  onToggle={() => toggleRule(rule.id)}
                  onUpdate={(patch) => updateRow(rule.id, patch)}
                />
              );
            })}
          </ul>
        )}
      </div>

      <p className="mt-3 text-xs text-fg-subtle">
        {activeCount} of {langRules.length} rules active
        {visible.length < langRules.length ? ` · ${visible.length} shown` : ''}
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Rule row
// ---------------------------------------------------------------------------

interface RuleRowProps {
  readonly rule: Rule;
  readonly state: RowState;
  readonly onToggle: () => void;
  readonly onUpdate: (patch: Partial<RowState>) => void;
}

function RuleRow({ rule, state, onToggle, onUpdate }: RuleRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li className={`transition-colors ${state.active ? 'bg-accent/[0.03]' : ''}`}>
      {/* Main row */}
      <div className="grid grid-cols-[2rem_1fr_7rem_8rem] gap-3 items-center px-4 py-2.5 group">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={state.active}
          onChange={onToggle}
          className="accent-accent h-4 w-4"
          aria-label={`Activate ${rule.id}`}
        />

        {/* Rule identity */}
        <div className="min-w-0">
          <p className="text-xs font-mono text-fg truncate">{rule.id}</p>
          <p className="text-xs text-fg-muted truncate">{rule.name}</p>
        </div>

        {/* Default severity chip */}
        <div className="flex justify-center">
          <SeverityChip severity={rule.severity} />
        </div>

        {/* Active severity override */}
        <div className="flex items-center gap-1">
          {state.active ? (
            <select
              value={state.severityOverride}
              onChange={(e) => onUpdate({ severityOverride: e.target.value })}
              className="w-full px-1.5 py-1 rounded border border-border bg-bg-inset text-xs text-fg focus:outline-none focus:ring-1 focus:ring-accent/40"
              aria-label="Severity override"
            >
              <option value="">— default —</option>
              {ISSUE_SEVERITIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-fg-subtle">—</span>
          )}
          {state.active && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              title="Edit params"
              className="p-0.5 rounded hover:bg-bg-subtle transition-colors text-fg-subtle opacity-0 group-hover:opacity-100"
            >
              {expanded
                ? <ChevronDown className="h-3.5 w-3.5" />
                : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Params editor (collapsible) */}
      {state.active && expanded && (
        <div className="px-10 pb-3">
          <ParamsEditor
            params={state.params}
            onChange={(params) => onUpdate({ params })}
          />
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Params key-value editor
// ---------------------------------------------------------------------------

interface ParamsEditorProps {
  readonly params: { key: string; value: string }[];
  readonly onChange: (params: { key: string; value: string }[]) => void;
}

function ParamsEditor({ params, onChange }: ParamsEditorProps) {
  const addRow = () => onChange([...params, { key: '', value: '' }]);

  const updateRow = (i: number, field: 'key' | 'value', val: string) => {
    const next = params.map((p, idx) => (idx === i ? { ...p, [field]: val } : p));
    onChange(next);
  };

  const removeRow = (i: number) => onChange(params.filter((_, idx) => idx !== i));

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="px-3 py-1.5 bg-bg-inset text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-subtle border-b border-border">
        Parameters
      </div>
      <div className="p-2 space-y-1.5">
        {params.map((p, i) => (
          <div key={p.key || String(i)} className="flex gap-2 items-center">
            <input
              type="text"
              value={p.key}
              onChange={(e) => updateRow(i, 'key', e.target.value)}
              placeholder="key"
              className={paramInputCls}
            />
            <span className="text-fg-subtle text-xs">=</span>
            <input
              type="text"
              value={p.value}
              onChange={(e) => updateRow(i, 'value', e.target.value)}
              placeholder="value"
              className={`${paramInputCls} flex-1`}
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="text-fg-subtle hover:text-danger transition-colors text-xs px-1"
              aria-label="Remove param"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          className="text-xs text-accent hover:opacity-80 transition-opacity"
        >
          + Add parameter
        </button>
      </div>
    </div>
  );
}

const paramInputCls =
  'px-2 py-1 rounded border border-border bg-bg-inset text-xs text-fg placeholder-fg-subtle focus:outline-none focus:ring-1 focus:ring-accent/40 w-28';

// ---------------------------------------------------------------------------
// Severity chip
// ---------------------------------------------------------------------------

const SEV_COLORS: Record<string, string> = {
  INFO: 'bg-fg-subtle/10 text-fg-subtle border-fg-subtle/20',
  MINOR: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  MAJOR: 'bg-warning/10 text-warning border-warning/20',
  CRITICAL: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  BLOCKER: 'bg-danger/10 text-danger border-danger/20',
};

function SeverityChip({ severity }: { readonly severity: string }) {
  const cls = SEV_COLORS[severity] ?? 'bg-fg-subtle/10 text-fg-subtle border-fg-subtle/20';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${cls}`}>
      {severity}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Loading / error states
// ---------------------------------------------------------------------------

function LoadingCard() {
  return (
    <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading profile…</span>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  const msg = error instanceof ApiError ? `${error.code}: ${error.message}` : String(error);
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
    </div>
  );
}
